<template>
  <div>
    <div
      v-if="isMobileLayout && isMobileSidebarOpen"
      class="sidebar-backdrop"
      @click="closeMobileSidebar"
    ></div>

    <nav class="client-sidebar" :class="sidebarClasses">
      <div class="sidebar-inner">
        <div v-if="isMobileLayout" class="sidebar-mobile-header">
          <router-link class="sidebar-brand" :to="homeRoute">
            {{ brandLabel }}
          </router-link>

          <button
            type="button"
            class="sidebar-close-btn"
            @click="closeMobileSidebar"
            aria-label="Close menu"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <router-link
          v-else
          class="sidebar-brand"
          :to="homeRoute"
        >
          {{ brandLabel }}
        </router-link>

        <hr class="sidebar-divider" />

        <h6 class="sidebar-heading">
          {{ brandLabel }} Pages
        </h6>

        <!-- Client menu -->
        <ul v-if="!isVendor" class="sidebar-menu">
          <li class="items-center">
            <router-link
              to="/main/dashboard"
              custom
              v-slot="{ href, navigate, isActive }"
            >
              <a
                :href="href"
                @click.prevent="handleNavigate(navigate)"
                class="sidebar-link"
                :class="
                  isActive
                    ? 'sidebar-link-active'
                    : 'sidebar-link-inactive'
                "
              >
                <i
                  :class="[
                    'fas fa-tv',
                    'sidebar-icon',
                    isActive
                      ? 'opacity-75'
                      : 'text-blueGray-300',
                  ]"
                ></i>

                Dashboard
              </a>
            </router-link>
          </li>

          <li
            v-for="section in clientSections"
            :key="section.key"
            class="sidebar-group"
          >
            <div class="sidebar-group-row">
              <router-link
                :to="section.to"
                custom
                v-slot="{ href, navigate }"
              >
                <a
                  :href="href"
                  @click.prevent="
                    handleSectionNavigate(
                      navigate,
                      section.key
                    )
                  "
                  class="sidebar-link sidebar-group-link"
                  :class="
                    isSectionActive(section)
                      ? 'sidebar-link-active'
                      : 'sidebar-link-inactive'
                  "
                >
                  <i
                    :class="[
                      section.icon,
                      'sidebar-icon',
                      isSectionActive(section)
                        ? 'opacity-75'
                        : 'text-blueGray-300',
                    ]"
                  ></i>

                  {{ section.label }}
                </a>
              </router-link>

              <button
                type="button"
                class="sidebar-section-toggle"
                :class="{
                  'sidebar-section-toggle-active':
                    isSectionActive(section),
                }"
                :aria-expanded="
                  openSections[section.key]
                    ? 'true'
                    : 'false'
                "
                :aria-label="
                  `${
                    openSections[section.key]
                      ? 'Collapse'
                      : 'Expand'
                  } ${section.label}`
                "
                @click="
                  toggleSection(section.key)
                "
              >
                <i
                  class="fas fa-chevron-down"
                  :class="{
                    'sidebar-chevron-open':
                      openSections[section.key],
                  }"
                ></i>
              </button>
            </div>

            <ul
              v-show="openSections[section.key]"
              class="sidebar-submenu"
            >
              <li
                v-for="child in section.children"
                :key="child.to"
              >
                <router-link
                  :to="child.to"
                  custom
                  v-slot="{
                    href,
                    navigate,
                    isActive,
                  }"
                >
                  <a
                    :href="href"
                    @click.prevent="
                      handleNavigate(navigate)
                    "
                    class="sidebar-sublink"
                    :class="
                      isActive
                        ? 'sidebar-sublink-active'
                        : 'sidebar-sublink-inactive'
                    "
                  >
                    <i
                      :class="[
                        child.icon,
                        'sidebar-subicon',
                      ]"
                    ></i>

                    {{ child.label }}
                  </a>
                </router-link>
              </li>
            </ul>
          </li>

          <li
            v-for="item in clientUtilityItems"
            :key="item.to"
            class="items-center"
          >
            <router-link
              :to="item.to"
              custom
              v-slot="{ href, navigate, isActive }"
            >
              <a
                :href="href"
                @click.prevent="handleNavigate(navigate)"
                class="sidebar-link"
                :class="
                  isActive
                    ? 'sidebar-link-active'
                    : 'sidebar-link-inactive'
                "
              >
                <i
                  :class="[
                    item.icon,
                    'sidebar-icon',
                    isActive
                      ? 'opacity-75'
                      : 'text-blueGray-300',
                  ]"
                ></i>

                {{ item.label }}
              </a>
            </router-link>
          </li>
        </ul>

        <!-- Vendor login menu -->
        <ul v-else class="sidebar-menu">
          <li
            v-for="item in vendorMenuItems"
            :key="item.to"
            class="items-center"
          >
            <router-link
              :to="item.to"
              custom
              v-slot="{
                href,
                navigate,
                isActive,
              }"
            >
              <a
                :href="href"
                @click.prevent="
                  handleNavigate(navigate)
                "
                class="sidebar-link"
                :class="
                  isActive
                    ? 'sidebar-link-active'
                    : 'sidebar-link-inactive'
                "
              >
                <i
                  :class="[
                    item.icon,
                    'sidebar-icon',
                    isActive
                      ? 'opacity-75'
                      : 'text-blueGray-300',
                  ]"
                ></i>

                {{ item.label }}
              </a>
            </router-link>
          </li>
        </ul>

        <hr class="sidebar-divider sidebar-footer-divider" />

        <ul class="sidebar-menu">
          <li class="items-center">
            <a
              @click="logout"
              class="sidebar-link sidebar-link-inactive cursor-pointer"
            >
              <i
                class="fas fa-sign-out-alt sidebar-icon text-blueGray-300"
              ></i>

              Logout
            </a>
          </li>
        </ul>
      </div>
    </nav>
  </div>
</template>

<script>
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/dist/sweetalert2.min.css";

export default {
  data() {
    return {
      isMobileSidebarOpen: false,

      windowWidth:
        typeof window !== "undefined"
          ? window.innerWidth
          : 1440,

      userType:
        typeof window !== "undefined"
          ? localStorage.getItem("user_type")
          : null,

      openSections: {
        budget: false,
        employee: false,
        vendor: false,
      },

      clientSections: [
        {
          key: "budget",
          label: "Budget Monitoring",
          to: "/main/budget_monitoring",
          icon: "fas fa-chart-pie",
          children: [
            {
              label: "My Expenditure",
              to: "/main/spending-history",
              icon: "fas fa-receipt",
            },
            {
              label: "Purchase Order",
              to: "/main/purchase-order",
              icon: "fas fa-shopping-cart",
            },
            {
              label: "My Documents",
              to: "/main/enrollment",
              icon: "fas fa-folder-open",
            },
          ],
        },
        {
          key: "employee",
          label: "Employee",
          to: "/main/hire_employees",
          icon: "fas fa-user",
          children: [
            {
              label: "Employees",
              to: "/main/hire_employees",
              icon: "fas fa-users",
            },
            {
              label: "Employee Enrollment",
              to: "/main/employee-enrollment",
              icon: "fas fa-user-plus",
            },
            {
              label: "Payroll",
              to: "/main/payroll",
              icon: "fas fa-money-check-alt",
            },
            {
              label: "Time Sheets",
              to: "/main/timesheets",
              icon: "fas fa-clock",
            },
          ],
        },
        {
          key: "vendor",
          label: "Vendor",
          to: "/main/vendors-hiring",
          icon: "fas fa-building",
          children: [
            {
              label: "Vendors",
              to: "/main/vendors-hiring",
              icon: "fas fa-building",
            },
            {
              label: "Vendors Invoice",
              to: "/main/vendor_invoice",
              icon: "fas fa-file-invoice-dollar",
            },
          ],
        },
      ],

      clientUtilityItems: [
        {
          label: "Message Center",
          to: "/main/message",
          icon: "fas fa-envelope",
        },
        {
          label: "Change Password",
          to: "/main/change_password",
          icon: "fas fa-key",
        },
      ],

      vendorMenuItems: [
        {
          label: "My Invoices",
          to: "/main/vendor_invoice_upload",
          icon: "fas fa-file-invoice-dollar",
        },
        {
          label: "Change Password",
          to: "/main/change_password",
          icon: "fas fa-key",
        },
      ],
    };
  },

  computed: {
    isVendor() {
      return this.userType === "vendor";
    },

    brandLabel() {
      return this.isVendor
        ? "Vendor"
        : "Client";
    },

    homeRoute() {
      return this.isVendor
        ? "/main/vendor_invoice_upload"
        : "/main/dashboard";
    },

    isMobileLayout() {
      return this.windowWidth < 1024;
    },

    sidebarClasses() {
      return {
        "client-sidebar-mobile-open":
          this.isMobileLayout &&
          this.isMobileSidebarOpen,

        "client-sidebar-mobile-closed":
          this.isMobileLayout &&
          !this.isMobileSidebarOpen,

        "client-sidebar-desktop":
          !this.isMobileLayout,
      };
    },
  },

  watch: {
    $route() {
      this.openActiveSection();

      if (this.isMobileLayout) {
        this.closeMobileSidebar();
      }
    },
  },

  methods: {
    isSectionActive(section) {
      const currentPath = this.$route.path;

      if (currentPath === section.to) {
        return true;
      }

      return section.children.some(
        (child) =>
          currentPath === child.to ||
          currentPath.startsWith(
            `${child.to}/`
          )
      );
    },

    openActiveSection() {
      if (this.isVendor) {
        return;
      }

      this.clientSections.forEach(
        (section) => {
          if (this.isSectionActive(section)) {
            this.openSections[
              section.key
            ] = true;
          }
        }
      );
    },

    toggleSection(sectionKey) {
      this.openSections[sectionKey] =
        !this.openSections[sectionKey];
    },

    handleSectionNavigate(
      navigate,
      sectionKey
    ) {
      this.openSections[sectionKey] = true;
      this.handleNavigate(navigate);
    },

    handleGlobalSidebarToggle() {
      if (this.isMobileLayout) {
        this.isMobileSidebarOpen =
          !this.isMobileSidebarOpen;

        this.syncBodyScroll();
      }
    },

    handleNavigate(navigate) {
      navigate();

      if (this.isMobileLayout) {
        this.closeMobileSidebar();
      }
    },

    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
      this.syncBodyScroll();
    },

    syncBodyScroll() {
      if (
        this.isMobileLayout &&
        this.isMobileSidebarOpen
      ) {
        document.body.style.overflow =
          "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;

      if (!this.isMobileLayout) {
        this.isMobileSidebarOpen = false;
        document.body.style.overflow = "";
      }
    },

    logout() {
      Swal.fire({
        title:
          "Are you sure you <br> want to log-out?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Log-out",
      }).then((result) => {
        if (result.isConfirmed) {
          localStorage.removeItem("token");
          localStorage.removeItem(
            "tokenExpiration"
          );
          localStorage.removeItem("portal");
          localStorage.removeItem(
            "user_type"
          );
          localStorage.removeItem(
            "employee_enrollment_draft"
          );
          localStorage.removeItem(
            "employee_enrollment_draft_id"
          );
          localStorage.removeItem(
            "employeeEnrollment"
          );
          localStorage.removeItem(
            "employeeEnrollmentForm"
          );
          localStorage.removeItem(
            "generatedPdfState"
          );

          Object.keys(localStorage)
            .filter((key) =>
              key.startsWith(
                "employee_enrollment_generated_forms_"
              )
            )
            .forEach((key) =>
              localStorage.removeItem(key)
            );

          this.closeMobileSidebar();

          this.$router.replace({
            path: "/auth/login",
          });
        }
      });
    },
  },

  mounted() {
    window.addEventListener(
      "bfms-toggle-sidebar",
      this.handleGlobalSidebarToggle
    );

    window.addEventListener(
      "resize",
      this.handleResize
    );

    this.handleResize();
    this.openActiveSection();
  },

  beforeUnmount() {
    window.removeEventListener(
      "bfms-toggle-sidebar",
      this.handleGlobalSidebarToggle
    );

    window.removeEventListener(
      "resize",
      this.handleResize
    );

    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
.client-sidebar {
  background: #ffffff;
  box-shadow: 0 10px 30px
    rgba(15, 23, 42, 0.12);
  z-index: 50;
}

.sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 45;
}

.sidebar-inner {
  height: 100%;
  overflow-y: auto;
  padding: 1rem 1.4rem;
}

.sidebar-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 1rem;
}

.sidebar-brand {
  color: #475569;
  font-size: 0.95rem;
  text-transform: uppercase;
  font-weight: 800;
  letter-spacing: 0.03em;
}

.sidebar-close-btn {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  color: #64748b;
  border: 1px solid #e2e8f0;
  background: #ffffff;
}

.sidebar-divider {
  margin: 1rem 0;
  border-color: #e2e8f0;
}

.sidebar-footer-divider {
  margin-top: 1.25rem;
}

.sidebar-heading {
  color: #94a3b8;
  font-size: 0.72rem;
  text-transform: uppercase;
  font-weight: 800;
  letter-spacing: 0.08em;
  padding: 0 0 0.75rem;
}

.sidebar-menu,
.sidebar-submenu {
  display: flex;
  flex-direction: column;
  list-style: none;
  min-width: 100%;
  margin: 0;
  padding: 0;
}

.sidebar-link {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.72rem 0;
  font-size: 0.9rem;
  text-transform: uppercase;
  font-weight: 700;
  transition: color 0.2s ease;
}

.sidebar-icon {
  width: 1.45rem;
  margin-right: 0.45rem;
  font-size: 0.85rem;
  text-align: center;
}

.sidebar-link-active {
  color: #10b981;
}

.sidebar-link-inactive {
  color: #334155;
}

.sidebar-link-inactive:hover {
  color: #64748b;
}

.sidebar-group {
  margin: 0;
}

.sidebar-group-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.sidebar-group-link {
  flex: 1;
  min-width: 0;
}

.sidebar-section-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin-left: 0.25rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.sidebar-section-toggle:hover {
  background: #f1f5f9;
  color: #475569;
}

.sidebar-section-toggle-active {
  color: #10b981;
}

.sidebar-section-toggle i {
  font-size: 0.7rem;
  transition: transform 0.2s ease;
}

.sidebar-chevron-open {
  transform: rotate(180deg);
}

.sidebar-submenu {
  margin: 0.05rem 0 0.45rem 1rem;
  padding-left: 0.8rem;
  border-left: 2px solid #e2e8f0;
}

.sidebar-sublink {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.55rem 0;
  font-size: 0.86rem;
  font-weight: 600;
  text-transform: none;
  transition:
    color 0.2s ease,
    padding-left 0.2s ease;
}

.sidebar-subicon {
  width: 1.3rem;
  margin-right: 0.45rem;
  font-size: 0.75rem;
  text-align: center;
}

.sidebar-sublink-active {
  color: #10b981;
}

.sidebar-sublink-inactive {
  color: #64748b;
}

.sidebar-sublink-inactive:hover {
  color: #334155;
  padding-left: 0.2rem;
}

.client-sidebar-mobile-open,
.client-sidebar-mobile-closed {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 288px;
  max-width: 85vw;
  transition: transform 0.28s ease;
}

.client-sidebar-mobile-open {
  transform: translateX(0);
}

.client-sidebar-mobile-closed {
  transform: translateX(-100%);
}

.client-sidebar-desktop {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 16rem;
  transform: none !important;
}

.client-sidebar-desktop
  .sidebar-inner {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
</style>
